import React from 'react';
import { Box, Typography } from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import {
  Lightbulb, AlertTriangle, Truck, ShoppingCart, Factory, PlayCircle,
  PackageMinus, IndianRupee, Cpu,
} from 'lucide-react';
import { PageHeader, StatusChip, Panel, StatStrip, ProgressRow, LoadGate, PageShell, CHART_FONT } from './businessUi';
import {
  computeAllMovements, computeStockLevels, computeMaterialPlan,
  computeProductionMovements, formatCurrency, formatNumber,
} from './erpData';
import { useErp } from './useErp';
import { ERP_KEYS } from '../../Services/app/erpservice';

const KEYS = {
  inventory: ERP_KEYS.inventory,
  sales: ERP_KEYS.sales,
  purchase: ERP_KEYS.purchase,
  production: ERP_KEYS.production,
  boms: ERP_KEYS.boms,
  manualMoves: ERP_KEYS.movements,
};

const PRIORITY = { critical: 0, action: 1, info: 2 };
const priorityTone = { critical: 'destructive', action: 'warning', info: 'sky' };
const priorityLabel = { critical: 'Critical', action: 'Do next', info: 'Info' };
const priorityColor = { critical: '#f43f5e', action: '#f59e0b', info: '#0ea5e9' };

export default function Insights() {
  const { data, loading, error, reload } = useErp(KEYS);

  const inventory = data?.inventory || [];
  const sales = data?.sales || [];
  const purchase = data?.purchase || [];
  const production = data?.production || [];
  const boms = data?.boms || [];
  const manualMoves = data?.manualMoves || [];

  const allMovements = computeAllMovements(manualMoves, production, boms);
  const levels = computeStockLevels(inventory, allMovements);
  const nameOf = (code) => inventory.find((i) => i.code === code)?.name
    || boms.find((b) => b.fg === code)?.name || code;

  // ---- Headline numbers --------------------------------------------------
  const stockValue = inventory.reduce((s, i) => s + (levels[i.code] || 0) * i.rate, 0);
  const openSales = sales.filter((o) => o.status !== 'Paid').reduce((s, o) => s + o.amount, 0);
  const openPurchase = purchase.filter((o) => o.status === 'Open' || o.status === 'Partially Received').reduce((s, o) => s + o.amount, 0);
  const wip = production.filter((o) => o.status === 'In Progress').length;
  const unitsProduced = production.filter((o) => o.status === 'Completed').reduce((s, o) => s + o.qty, 0);

  // ---- WHAT TO DO NEXT (action center) -----------------------------------
  const actions = [];

  // Production blocked by / ready from material availability.
  production.filter((o) => o.status === 'Planned' || o.status === 'In Progress').forEach((o) => {
    const plan = computeMaterialPlan(o, boms, inventory, levels);
    const shorts = plan.filter((p) => p.shortfall > 0);
    if (shorts.length) {
      shorts.forEach((s) => actions.push({
        priority: 'critical', icon: AlertTriangle,
        title: `Material short for ${o.id}`,
        detail: `Need ${formatNumber(s.shortfall, s.shortfall % 1 ? 2 : 0)} ${s.uom} more of ${s.name} (${s.code}) to run ${o.id} on ${o.machine}.`,
        cta: 'Raise a Purchase Order',
      }));
    } else if (o.status === 'Planned' && boms.find((b) => b.fg === o.fg)) {
      actions.push({
        priority: 'action', icon: PlayCircle,
        title: `Ready to start ${o.id}`,
        detail: `All materials available for ${formatNumber(o.qty)} × ${nameOf(o.fg)} on ${o.machine}.`,
        cta: 'Start production',
      });
    }
  });

  // Reorder alerts.
  inventory.filter((i) => (levels[i.code] || 0) < i.reorder).forEach((i) => {
    const out = (levels[i.code] || 0) <= 0;
    actions.push({
      priority: out ? 'critical' : 'action', icon: PackageMinus,
      title: `${out ? 'Out of stock' : 'Low stock'}: ${i.name}`,
      detail: `On hand ${formatNumber(levels[i.code] || 0)} ${i.uom}, reorder level ${formatNumber(i.reorder)}.`,
      cta: 'Reorder from supplier',
    });
  });

  // Open purchase orders awaiting receipt.
  purchase.filter((o) => o.status === 'Open' || o.status === 'Partially Received').forEach((o) => {
    actions.push({
      priority: 'action', icon: Truck,
      title: `Awaiting goods: ${o.id}`,
      detail: `${formatCurrency(o.amount)} from ${o.supplier}, ordered ${o.date}.`,
      cta: 'Receive / follow up',
    });
  });

  // Sales orders to progress or collect.
  sales.forEach((o) => {
    if (o.status === 'Draft') {
      actions.push({ priority: 'action', icon: ShoppingCart, title: `Confirm order ${o.id}`, detail: `${o.customer} · ${formatCurrency(o.amount)} still in draft.`, cta: 'Confirm & schedule' });
    } else if (o.status === 'Invoiced') {
      actions.push({ priority: 'action', icon: IndianRupee, title: `Collect payment ${o.id}`, detail: `${o.customer} · ${formatCurrency(o.amount)} invoiced, awaiting payment.`, cta: 'Follow up payment' });
    }
  });

  actions.sort((a, b) => PRIORITY[a.priority] - PRIORITY[b.priority]);
  const criticalCount = actions.filter((a) => a.priority === 'critical').length;

  // ---- HOW THE FACTORY IS PRODUCING --------------------------------------
  const statusList = ['Planned', 'In Progress', 'Completed'];
  const statusCounts = statusList.map((s) => production.filter((o) => o.status === s).length);
  const statusOptions = {
    chart: { type: 'donut', fontFamily: CHART_FONT, dropShadow: { enabled: true, top: 4, blur: 8, opacity: 0.12 } },
    labels: statusList,
    colors: ['#0ea5e9', '#f59e0b', '#10b981'],
    legend: { position: 'bottom', fontSize: '12px' },
    dataLabels: { enabled: false },
    stroke: { width: 2 },
    plotOptions: { pie: { donut: { size: '70%', labels: { show: true, total: { show: true, label: 'Orders', fontSize: '12px', color: 'text.secondary', formatter: () => String(production.length) } } } } },
  };

  const byMachine = {};
  production.forEach((o) => { byMachine[o.machine] = (byMachine[o.machine] || 0) + o.qty; });
  const machineEntries = Object.entries(byMachine).sort((a, b) => b[1] - a[1]);
  const maxMachine = Math.max(1, ...machineEntries.map((m) => m[1]));

  // ---- WHAT IT CONSUMED (from completed production) ----------------------
  const consumed = {};
  computeProductionMovements(production, boms).filter((m) => m.type === 'OUT').forEach((m) => {
    consumed[m.code] = (consumed[m.code] || 0) + m.qty;
  });
  const consumedEntries = Object.entries(consumed).sort((a, b) => b[1] - a[1]);
  const maxConsumed = Math.max(1, ...consumedEntries.map((c) => c[1]));

  const empty = inventory.length === 0 && production.length === 0 && sales.length === 0 && purchase.length === 0;

  return (
    <PageShell>
      <PageHeader
        title="Insights"
        subtitle="What your data is telling you — production health & the next best actions"
      />

      <LoadGate loading={loading} error={error} onRetry={reload}>
        {empty ? (
          <Panel title="Nothing to analyse yet" accent="linear-gradient(90deg,#7c5cff,#818cf8)">
            <Typography sx={{ p: 4, textAlign: 'center', color: 'text.disabled', fontSize: 14 }}>
              Add inventory, BOMs and orders in the other screens — this page will then summarise how the factory is running and tell you what to do next.
            </Typography>
          </Panel>
        ) : (
          <>
            <Box sx={{ mb: 2.5 }}>
              <StatStrip
                items={[
                  { label: 'Stock Value', value: formatCurrency(stockValue), tone: 'primary' },
                  { label: 'Open Sales', value: formatCurrency(openSales), tone: 'fuchsia', hint: 'to fulfil & collect' },
                  { label: 'Committed Purchases', value: formatCurrency(openPurchase), tone: 'sky', hint: 'awaiting receipt' },
                  { label: 'Work in Progress', value: String(wip), tone: 'warning', hint: 'orders running' },
                  { label: 'Units Produced', value: formatNumber(unitsProduced), tone: 'success' },
                ]}
              />
            </Box>

            {/* Action center */}
            <Panel
              title="What to do next"
              subtitle={actions.length ? `${actions.length} recommendation(s)${criticalCount ? ` · ${criticalCount} critical` : ''}, highest priority first` : 'No pending actions'}
              accent="linear-gradient(90deg,#f43f5e,#fb7185)"
              actions={<Lightbulb size={18} color="#f59e0b" />}
              sx={{ mb: 2.5 }}
            >
              <Box sx={{ px: 2, pb: 1.5 }}>
                {actions.length === 0 && (
                  <Typography sx={{ fontSize: 13, color: '#047857', py: 2 }}>
                    All clear — no shortages, low stock, open POs or pending orders. 🎉
                  </Typography>
                )}
                {actions.map((a, idx) => {
                  const Icon = a.icon;
                  return (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.25, borderBottom: idx < actions.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                      <Box sx={{ width: 36, height: 36, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'background.paper', background: priorityColor[a.priority] }}>
                        <Icon size={18} />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary' }}>{a.title}</Typography>
                          <StatusChip label={priorityLabel[a.priority]} tone={priorityTone[a.priority]} />
                        </Box>
                        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>{a.detail}</Typography>
                      </Box>
                      <Box sx={{ flexShrink: 0, alignSelf: 'center' }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: priorityColor[a.priority] }}>{a.cta} →</Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Panel>

            {/* How it's producing + what it consumed */}
            <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, mb: 2.5 }}>
              <Panel title="How the factory is producing" subtitle="Work-order status & machine load" accent="linear-gradient(90deg,#f59e0b,#fbbf24)" actions={<Factory size={18} color="#f59e0b" />}>
                <Box sx={{ px: 2, pb: 2 }}>
                  {production.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: 'text.disabled', py: 3, textAlign: 'center' }}>No production orders yet.</Typography>
                  ) : (
                    <>
                      <ReactApexChart options={statusOptions} series={statusCounts} type="donut" height={210} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1, mb: 1 }}>
                        <Cpu size={14} color="#64748b" />
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Output by machine (units)</Typography>
                      </Box>
                      {machineEntries.map(([m, qty]) => (
                        <ProgressRow key={m} label={m} value={qty} max={maxMachine} tone="warning" valueLabel={`${formatNumber(qty)} nos`} />
                      ))}
                    </>
                  )}
                </Box>
              </Panel>

              <Panel title="What it consumed to build it" subtitle="Material drawn from stock by completed orders" accent="linear-gradient(90deg,#10b981,#34d399)" actions={<PackageMinus size={18} color="#10b981" />}>
                <Box sx={{ px: 2, pt: 1.5, pb: 2 }}>
                  {consumedEntries.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: 'text.disabled', py: 3, textAlign: 'center' }}>
                      No completed production yet. Mark an order <strong>Completed</strong> to see consumption here.
                    </Typography>
                  ) : (
                    consumedEntries.map(([code, qty]) => {
                      const item = inventory.find((i) => i.code === code);
                      return (
                        <ProgressRow
                          key={code}
                          label={`${nameOf(code)} (${code})`}
                          value={qty} max={maxConsumed} tone="success"
                          valueLabel={`${formatNumber(qty, qty % 1 ? 2 : 0)} ${item ? item.uom : ''}`}
                        />
                      );
                    })
                  )}
                </Box>
              </Panel>
            </Box>
          </>
        )}
      </LoadGate>
    </PageShell>
  );
}
